/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import SeparatorList, { SEPARATOR_TYPES } from 'components/uiLibrary/SeparatorList';
import Typography from 'components/uiLibrary/Typography';

import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';

import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from 'constants/endpointConstants';
import { AGGREGATION_TYPES, PROFESSION_IDS, TP, WORK_CREATOR_PROFESSION_TYPES } from 'constants/index';

import { useQuery } from 'utils/react-query';
import { getCreatorsName, getWorkComposerList, getWorkComposers, getWorkTitle } from 'utils/works';

import searchQueries from 'containers/Search/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import AggregationField from '../AggregationField';

import classes from './ComposerWorkField.module.scss';

const WORK_CREATOR_PROFESSION_NAME = {
  [PROFESSION_IDS.COMPOSER]: `${TP}.m_COMPOSER`,
  [PROFESSION_IDS.LIBRETTIST]: `${TP}.LIBRETTISTS`,
};

const RenderWorkMenuItem = ({ props, option, trackingData, onClick }) => {
  const composers = getCreatorsName(option);

  return (
    <li {...props} className={classes.workMenuItem} {...(typeof onClick === 'function' && { onClick })}>
      <span className={classes.workName}>
        <EntityName
          entity={option}
          entityType={ENTITY_TYPES.WORK}
          trackingData={trackingData}
          typographyProps={{
            work: {
              size: 12,
            },
            translation: {
              size: 10,
            },
          }}
          withComposers={false}
        />
      </span>
      <Typography size={12} color="quinary">
        <SeparatorList
          separator={SEPARATOR_TYPES.BULLET}
          styles={{ item: classes.composerName }}
          data={composers?.map(composer => (
            <EntityName
              name={composer?.name}
              entityType={composer.entityType}
              entity={composer}
              trackingData={trackingData}
              isRaw
            />
          ))}
        />
      </Typography>
      <Typography size="10" color="secondary" textTransform="uppercase">
        {option?.workTypes?.[0]?.name}
      </Typography>
    </li>
  );
};

const RenderMenuItemComposer = ({ innerProps, option, trackingData, professionName }) => (
  <li {...innerProps} className={classes.composerMenuList}>
    <EntityName entity={option} entityType={ENTITY_TYPES.PROFILE} isRaw trackingData={trackingData} />
    <Typography size={10} color="quinary">
      {professionName}
    </Typography>
  </li>
);

const RenderComposerMobileOptions = ({ options, onChangeHandler, trackingData }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (!options?.length) {
    return null;
  }

  return (
    <ul className={classes.composerDrawer}>
      {options?.map(option => (
        <li
          key={option.id}
          onClick={() => {
            onChangeHandler(null, option);
          }}
        >
          <EntityName entity={option} entityType={ENTITY_TYPES.PROFILE} isRaw trackingData={trackingData} />
          <Typography size={10} color="quinary">
            {t(WORK_CREATOR_PROFESSION_NAME[option?.profession?.id])}
          </Typography>
        </li>
      ))}
    </ul>
  );
};

const ComposerWorkField = ({
  composerId,
  workId,
  onChange,
  showComposerField = false,
  styles,
  trackingData = {},
  handleCloseDrawer = () => {},
  showLabel = true,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const isMobile = useMediaQuery('(max-width: 1000px)');
  const [selectedComposer, setSelectedComposer] = useState(null);
  const [selectedWork, setSelectedWork] = useState(null);

  useEffect(() => {
    if (composerId === null) {
      setSelectedComposer(null);
    }
  }, [composerId]);

  useEffect(() => {
    if (workId === null) {
      setSelectedWork(null);
      setSelectedComposer(null);
    }
  }, [workId]);

  const onSelectWork = useCallback(
    work => {
      let workObj = null;

      if (work?.id) {
        workObj = {
          ...work,
          label: getWorkTitle(work, true, true),
        };

        if (!selectedComposer?.id) {
          const workComposerList = getWorkComposerList(work);
          const creatorsLabel = getWorkComposers(work);
          let composerObj = { isCleared: true };

          if (creatorsLabel) {
            const profileId = workComposerList?.[0]?.profile?.id;
            const orgId = workComposerList?.[0]?.organization?.id;

            composerObj = {
              id: profileId || orgId,
              label: creatorsLabel,
              entityType: profileId ? ENTITY_TYPES.PROFILE : ENTITY_TYPES.ORGANIZATION,
            };
          }

          setSelectedComposer(composerObj);
        }
      }

      setSelectedWork(workObj);

      if (onChange) {
        onChange(workObj);
      }
    },
    [onChange, selectedComposer],
  );

  const onSelectComposer = useCallback(composer => {
    if (composer?.id) {
      setSelectedComposer(composer);
    } else {
      setSelectedComposer({ isCleared: true });
    }
  }, []);

  const { data: fetchedComposer } = useQuery(
    searchQueries.getSelectedOption({
      identifier: composerId,
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        work_creator_id: composerId,
        aggregation_type: AGGREGATION_TYPES.CREATOR,
      },
      queryConfig: {
        enabled: !!composerId && selectedComposer?.id !== composerId,
      },
    }),
  );

  const { data: fetchedWork } = useQuery(
    searchQueries.getSelectedOption({
      identifier: workId,
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        work_id: workId,
        aggregation_type: AGGREGATION_TYPES.WORK,
      },
      queryConfig: {
        enabled: !!workId && selectedWork?.id !== workId,
      },
    }),
  );

  const { workValue, composerValue } = useMemo(() => {
    let work = null;
    let composer = null;

    if (fetchedWork && fetchedWork?.id === workId) {
      work = fetchedWork;
      work.label = getWorkTitle(fetchedWork, true, true);
    }

    if (selectedWork) {
      work = selectedWork;
    }

    if (fetchedComposer?.id === composerId) {
      composer = fetchedComposer;
    }

    if (selectedComposer) {
      composer = selectedComposer;
    }

    if (!composer && work && !selectedComposer?.isCleared) {
      const creatorsLabel = getWorkComposers(work);
      composer = {
        label: creatorsLabel,
        name: creatorsLabel,
      };
    }

    return {
      workValue: work,
      composerValue: composer,
    };
  }, [selectedWork, workId, fetchedComposer, selectedComposer, composerId, fetchedWork]);

  return (
    <div className={classnames(classes.container, { [styles.container]: !!styles.container })}>
      {showComposerField && (
        <div className={classnames(classes.fieldWrapper, { [styles.fieldWrapper]: !!styles.fieldWrapper })}>
          {showLabel && (
            <Typography size={12} weight="medium" className={classes.fieldWrapper__title}>
              {t(`${TP}.FN_COMPOSER_CREATOR`)}
            </Typography>
          )}
          <AggregationField
            endpoint={FILTER_AGGREGATION_SEARCH(PRODUCTIONS)}
            aggregationType={AGGREGATION_TYPES.CREATOR}
            selectedOptions={composerValue ? [composerValue] : []}
            onChange={onSelectComposer}
            queryParams={{
              work_creator_profession: WORK_CREATOR_PROFESSION_TYPES.COMPOSER,
              ...(workId && { work_id: workId }),
            }}
            renderMenuListItem={(innerProps, option, Null, searchTrackingData) => (
              <RenderMenuItemComposer
                innerProps={innerProps}
                option={option}
                trackingData={searchTrackingData}
                professionName={t(WORK_CREATOR_PROFESSION_NAME[option?.profession?.id])}
              />
            )}
            styles={styles}
            trackingData={trackingData}
            popperStyles={classes.popperStyles}
            textFieldProps={{
              typingPlaceholder: t(`${TP}.TYPE_TO_SEARCH`),
              ...(isMobile && { placeholder: t(`${TP}.COMPOSER_SEARCH_PLACEHOLDER`) }),
            }}
            {...(isMobile && {
              mobileDrawerProps: {
                allowMobileDrawer: true,
                showDrawerSearch: true,
                drawerHeaderTitle: `${t(`${TP}.FN_COMPOSER_CREATOR`)}`,
                drawerSearchPlaceholder: t(`${TP}.COMPOSER_SEARCH_PLACEHOLDER`),
                renderMobileOptions: (options, onChangeHandler) => (
                  <RenderComposerMobileOptions options={options} onChangeHandler={onChangeHandler} />
                ),
              },
            })}
          />
        </div>
      )}
      <div className={classnames(classes.fieldWrapper, { [styles.workFieldWrapper]: !!styles.workFieldWrapper })}>
        {showLabel && (
          <Typography size={12} weight="medium" className={classes.fieldWrapper__title}>
            {t(`${TP}.m_MUSICALWORK`)}
          </Typography>
        )}
        <AggregationField
          endpoint={FILTER_AGGREGATION_SEARCH(PRODUCTIONS)}
          aggregationType={AGGREGATION_TYPES.WORK}
          selectedOptions={workValue ? [workValue] : []}
          onChange={onSelectWork}
          queryParams={{
            work_creator_profession: WORK_CREATOR_PROFESSION_TYPES.COMPOSER,
            ...(composerValue?.id && {
              work_creator_id: composerValue?.id.toString().match(/^[ao]/)
                ? composerValue?.id
                : `${composerValue?.entityType === ENTITY_TYPES.PROFILE ? 'a' : 'o'}${composerValue?.id}`,
            }),
          }}
          renderMenuListItem={(props, option) => (
            <RenderWorkMenuItem props={props} key={option.id} option={option} trackingData={trackingData} />
          )}
          styles={styles}
          trackingData={{ ...trackingData, subComponent: SUB_COMPONENTS.MUSICAL_WORK }}
          popperStyles={classes.popperStyles}
          textFieldProps={{
            typingPlaceholder: t(`${TP}.TYPE_TO_SEARCH`),
            ...(isMobile && { placeholder: t(`${TP}.MUSICAL_WORK_SEARCH_PLACEHOLDER`) }),
          }}
          {...(isMobile && {
            mobileDrawerProps: {
              allowMobileDrawer: true,
              showDrawerSearch: true,
              drawerHeaderTitle: `${t(`${TP}.m_MUSICALWORK`)}`,
              drawerSearchPlaceholder: t(`${TP}.MUSICAL_WORK_SEARCH_PLACEHOLDER`),
              renderMobileOptions: (options, onChangeHandler) => (
                <ul className={classes.composerDrawer}>
                  {options?.map(option => (
                    <RenderWorkMenuItem
                      key={option.id}
                      option={option}
                      trackingData={trackingData}
                      onClick={() => {
                        onChangeHandler(null, option);
                        handleCloseDrawer();
                      }}
                    />
                  ))}
                </ul>
              ),
            },
          })}
        />
      </div>
    </div>
  );
};

export default ComposerWorkField;
